/**
 * Bin/cable labels for temperature comparison PDF filters.
 * Primary: site.data.bins[].cableList (same as SensorTemperatureGraphFilter).
 * Fallback: site.layoutCables from fetchUserSite (Temputech layout) — often populated when bins[].cableList is empty.
 */

function uniqueSortedNames(names) {
    const set = new Set(
        (names || [])
            .map((n) => (typeof n === 'string' ? n.trim() : String(n ?? '').trim()))
            .filter(Boolean),
    );
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function numericSortKey(x) {
    if (x == null || x === '') return Number.POSITIVE_INFINITY;
    const n = Number(x);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function cableIdSortKey(c) {
    if (c == null || typeof c !== 'object') return Number.POSITIVE_INFINITY;
    const id = c?.id ?? c?.Id ?? c?.cableId ?? c?.CableId;
    return numericSortKey(id);
}

function mergeLabelPairsByMinCableId(pairs) {
    /** @type Map<string, { lab: string, id: number }> */
    const m = new Map();
    for (const { lab, id } of pairs) {
        if (!lab) continue;
        const k = lab.toLowerCase();
        const prev = m.get(k);
        if (!prev || id < prev.id) m.set(k, { lab, id });
    }
    return [...m.values()]
        .sort((a, b) => a.id - b.id || a.lab.localeCompare(b.lab, undefined, { sensitivity: 'base' }))
        .map((x) => x.lab);
}

function coerceCableList(raw) {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            const p = JSON.parse(raw);
            return Array.isArray(p) ? p : [];
        } catch {
            return [];
        }
    }
    if (typeof raw === 'object') {
        const n = raw.nodes ?? raw.Nodes ?? raw.items ?? raw.Items ?? raw.data ?? raw.Data;
        if (Array.isArray(n)) return n;
        const vals = Object.values(raw);
        if (vals.length > 0 && vals.every((x) => x != null && typeof x === 'object')) {
            return vals;
        }
    }
    return [];
}

/** Same display priority as history graph: name, then number, id, first sensor name. */
export function cableDisplayLikeTemperatureHistory(c) {
    if (c == null) return '';
    if (typeof c === 'string' || typeof c === 'number') return String(c).trim();
    if (typeof c !== 'object') return '';
    const n = c.name ?? c.Name;
    if (n != null && String(n).trim() !== '') return String(n).trim();
    const num = c.number ?? c.Number;
    if (num != null && String(num).trim() !== '') return String(num).trim();
    const id = c.id ?? c.Id;
    if (id != null && String(id).trim() !== '') return String(id).trim();
    const sl = c.sensorList ?? c.SensorList;
    if (Array.isArray(sl) && sl.length > 0) {
        const sn = sl[0]?.name ?? sl[0]?.Name;
        if (sn != null && String(sn).trim() !== '') return String(sn).trim();
    }
    return '';
}

function binNameMatches(want, ...candidates) {
    if (want == null || String(want).trim() === '') return true;
    const w = String(want).trim();
    for (const cand of candidates) {
        const t = String(cand ?? '').trim();
        if (!t) continue;
        if (t.localeCompare(w, undefined, { sensitivity: 'base' }) === 0) return true;
    }
    return false;
}

function pushCableLabelPairsFromBin(b, filterBinName, binTagCandidates, pairs) {
    if (!binNameMatches(filterBinName, ...binTagCandidates)) return;
    const list = coerceCableList(b.cableList ?? b.CableList);
    for (const c of list) {
        const lab = cableDisplayLikeTemperatureHistory(c);
        if (lab) pairs.push({ lab, id: cableIdSortKey(c) });
    }
}

/**
 * Bin names: `data.bins[].name` (history graph), plus 3D `site.bins[].binName` when needed.
 * Ordered by BTS / layout bin id (ascending); names without id sort last.
 */
export function getPdfReportBinNamesFromSiteData(site) {
    /** @type Map<string, { id: number, name: string }> */
    const byName = new Map();
    function add(binId, rawName) {
        const name = String(rawName ?? '').trim();
        if (!name) return;
        const id = numericSortKey(binId);
        const k = name.toLowerCase();
        const prev = byName.get(k);
        if (!prev || id < prev.id) byName.set(k, { id, name });
    }
    const bins = site?.data?.bins ?? site?.data?.Bins;
    if (Array.isArray(bins)) {
        for (const b of bins) {
            add(b?.binId ?? b?.BinId, b?.name ?? b?.Name);
        }
    }
    const tb = site?.bins ?? site?.Bins;
    if (Array.isArray(tb)) {
        for (const b of tb) {
            add(b?.binDataId ?? b?.BinDataId ?? b?.binId ?? b?.BinId, b?.binName ?? b?.BinName ?? b?.name ?? b?.Name);
        }
    }
    const lbs = site?.layoutBins ?? site?.LayoutBins;
    if (Array.isArray(lbs)) {
        for (const lb of lbs) {
            const lid = lb?.customerSiteLayoutBinId ?? lb?.CustomerSiteLayoutBinId;
            add(lid, lb?.binName ?? lb?.BinName);
        }
    }
    return [...byName.values()]
        .sort((a, b) => a.id - b.id || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        .map((x) => x.name);
}

/** BTS bin id -> display name (PDF filter matches dbo.Bin.Name). */
function buildBtsBinIdToNameMap(site) {
    /** @type Map<number, string> */
    const m = new Map();
    const db = site?.data?.bins ?? site?.data?.Bins;
    if (Array.isArray(db)) {
        for (const b of db) {
            const id = b?.binId ?? b?.BinId;
            const n = String(b?.name ?? b?.Name ?? '').trim();
            if (id != null && id !== '' && n) {
                m.set(Number(id), n);
            }
        }
    }
    const tb = site?.bins ?? site?.Bins;
    if (Array.isArray(tb)) {
        for (const b of tb) {
            const id = b?.binDataId ?? b?.BinDataId ?? b?.binId ?? b?.BinId;
            const n = String(b?.binName ?? b?.BinName ?? b?.name ?? b?.Name ?? '').trim();
            if (id != null && id !== '' && n) {
                m.set(Number(id), n);
            }
        }
    }
    return m;
}

/** customerSiteLayoutBinId -> binName from layout table. */
function buildLayoutBinIdToNameMap(site) {
    /** @type Map<number, string> */
    const m = new Map();
    const lbs = site?.layoutBins ?? site?.LayoutBins;
    if (!Array.isArray(lbs)) return m;
    for (const lb of lbs) {
        const lid = lb?.customerSiteLayoutBinId ?? lb?.CustomerSiteLayoutBinId;
        const n = String(lb?.binName ?? lb?.BinName ?? '').trim();
        if (lid != null && lid !== '' && n) {
            m.set(Number(lid), n);
        }
    }
    return m;
}

function layoutCableLabel(lc, site) {
    let cn = String(lc?.cableName ?? lc?.CableName ?? '').trim();
    if (cn) return cn;
    const cid = lc?.cableId ?? lc?.CableId;
    if (cid == null || cid === '') return '';
    const flat = site?.cables ?? site?.Cables;
    if (Array.isArray(flat)) {
        const found = flat.find((c) => (c?.id ?? c?.Id) === cid || Number(c?.id ?? c?.Id) === Number(cid));
        if (found) {
            const lab = cableDisplayLikeTemperatureHistory(found);
            if (lab) return lab;
        }
    }
    return '';
}

function resolveBinDisplayForLayoutCable(lc, btsIdToName, layoutIdToName) {
    const bts = lc?.binId ?? lc?.BinId ?? lc?.binDataId ?? lc?.BinDataId;
    if (bts != null && bts !== '') {
        const n = btsIdToName.get(Number(bts));
        if (n) return n;
    }
    const lub = lc?.customerSiteLayoutBinId ?? lc?.CustomerSiteLayoutBinId;
    if (lub != null && lub !== '') {
        const n = layoutIdToName.get(Number(lub));
        if (n) return n;
    }
    return '';
}

function pushCableNamePairsFromLayoutCables(site, filterBinName, pairs) {
    const list = site?.layoutCables ?? site?.LayoutCables;
    if (!Array.isArray(list) || list.length === 0) return;
    const want = filterBinName ? String(filterBinName).trim() : '';
    const btsIdToName = buildBtsBinIdToNameMap(site);
    const layoutIdToName = buildLayoutBinIdToNameMap(site);
    for (const lc of list) {
        const lab = layoutCableLabel(lc, site);
        if (!lab) continue;
        const binDisp = resolveBinDisplayForLayoutCable(lc, btsIdToName, layoutIdToName);
        if (
            want &&
            binDisp &&
            binDisp.localeCompare(want, undefined, { sensitivity: 'base' }) !== 0
        ) {
            continue;
        }
        const cid = lc?.cableId ?? lc?.CableId ?? lc?.id ?? lc?.Id;
        pairs.push({ lab, id: numericSortKey(cid) });
    }
}

/**
 * Distinct cable labels from layoutSensors (sensor rows) when layoutCables names are missing.
 * Uses site.cables by cableId for BTS cable name when possible.
 */
function pushCableNamePairsFromLayoutSensors(site, filterBinName, pairs) {
    const sensors = site?.layoutSensors ?? site?.LayoutSensors;
    if (!Array.isArray(sensors) || sensors.length === 0) return;
    const want = filterBinName ? String(filterBinName).trim() : '';
    const btsIdToName = buildBtsBinIdToNameMap(site);
    /** @type Map<string|number, { binId: *, cableId: * }> */
    const byCable = new Map();
    for (const s of sensors) {
        const cid = s?.cableId ?? s?.CableId;
        if (cid == null || cid === '') continue;
        if (!byCable.has(cid)) {
            byCable.set(cid, { binId: s?.binId ?? s?.BinId, cableId: cid });
        }
    }
    for (const { binId, cableId } of byCable.values()) {
        let binDisp = '';
        if (binId != null && binId !== '') {
            binDisp = btsIdToName.get(Number(binId)) ?? '';
        }
        if (
            want &&
            binDisp &&
            binDisp.localeCompare(want, undefined, { sensitivity: 'base' }) !== 0
        ) {
            continue;
        }
        const flat = site?.cables ?? site?.Cables;
        let lab = '';
        let idKey = numericSortKey(cableId);
        if (Array.isArray(flat)) {
            const found = flat.find((c) => (c?.id ?? c?.Id) === cableId || Number(c?.id ?? c?.Id) === Number(cableId));
            if (found) {
                lab = cableDisplayLikeTemperatureHistory(found);
                idKey = cableIdSortKey(found);
            }
        }
        if (!lab) continue;
        pairs.push({ lab, id: idKey });
    }
}

/**
 * Cable names: first `site.data.bins` (same as SensorTemperatureGraphFilter); if none, `site.bins` + binName; then flat `site.cables` when filter is All.
 * Ordered by cable id (ascending) when ids exist.
 */
export function getPdfReportCableNamesFromSiteData(site, filterBinName) {
    /** @type Array<{ lab: string, id: number }> */
    const pairs = [];
    const want = filterBinName ? String(filterBinName).trim() : '';

    const db = site?.data?.bins ?? site?.data?.Bins;
    if (Array.isArray(db)) {
        for (const b of db) {
            pushCableLabelPairsFromBin(b, want, [b?.name, b?.Name, b?.binName, b?.BinName], pairs);
        }
    }

    const tb = site?.bins ?? site?.Bins;
    if (Array.isArray(tb)) {
        for (const b of tb) {
            pushCableLabelPairsFromBin(b, want, [b?.binName, b?.BinName, b?.name, b?.Name], pairs);
        }
    }

    if (!want) {
        const flat = site?.cables ?? site?.Cables;
        if (Array.isArray(flat)) {
            for (const c of flat) {
                const lab = cableDisplayLikeTemperatureHistory(c);
                if (lab) pairs.push({ lab, id: cableIdSortKey(c) });
            }
        }
    }

    pushCableNamePairsFromLayoutCables(site, filterBinName, pairs);
    pushCableNamePairsFromLayoutSensors(site, filterBinName, pairs);

    return mergeLabelPairsByMinCableId(pairs);
}

function normalizeGuidForSiteInfos(g) {
    return String(g ?? '').replace(/[{}]/g, '').toLowerCase();
}

/**
 * Cables from `global.gw.siteInfos[].cables` (CustomerSites / fetchUserAttribute): id, binId, name or cableName.
 * Filter by bin when `filterBinName` is set (matches `siteInfos[].bins[].name` to binId).
 */
export function getSiteInfosCableRows(siteInfos, selectedSiteGuid, filterBinName) {
    const want = normalizeGuidForSiteInfos(selectedSiteGuid);
    const entry = (siteInfos || []).find((s) => normalizeGuidForSiteInfos(s?.siteGuid) === want);
    if (!entry || !Array.isArray(entry.cables) || entry.cables.length === 0) {
        return [];
    }
    const bins = entry.bins ?? entry.Bins ?? [];
    const wantBin = filterBinName ? String(filterBinName).trim() : '';
    let filterBinId = null;
    if (wantBin) {
        for (const b of bins) {
            const bn = String(b?.name ?? b?.Name ?? '').trim();
            if (bn.localeCompare(wantBin, undefined, { sensitivity: 'base' }) === 0) {
                filterBinId = b?.binId ?? b?.BinId;
                break;
            }
        }
    }
    const rows = [];
    for (const c of entry.cables) {
        const cid = c?.id ?? c?.Id;
        if (cid === undefined || cid === null) continue;
        const bid = c?.binId ?? c?.BinId;
        const nm = String(c?.cableName ?? c?.CableName ?? c?.name ?? c?.Name ?? '').trim();
        if (!nm) continue;
        if (filterBinId != null && filterBinId !== '' && Number(bid) !== Number(filterBinId)) {
            continue;
        }
        rows.push({
            id: Number(cid),
            binId: bid != null && bid !== '' ? Number(bid) : null,
            cableName: nm,
        });
    }
    rows.sort((a, b) => a.id - b.id || a.cableName.localeCompare(b.cableName, undefined, { sensitivity: 'base' }));
    return rows;
}

/** Bin display name for dropdown when a cable id is known (`siteInfos[].bins` matched by `binId`). */
export function getBinDisplayNameForSiteInfosCableId(siteInfos, selectedSiteGuid, cableId) {
    const want = normalizeGuidForSiteInfos(selectedSiteGuid);
    const entry = (siteInfos || []).find((s) => normalizeGuidForSiteInfos(s?.siteGuid) === want);
    if (!entry || !Array.isArray(entry.cables)) return null;
    const cable = entry.cables.find((c) => String(c?.id ?? c?.Id) === String(cableId));
    if (!cable) return null;
    const bid = cable?.binId ?? cable?.BinId;
    if (bid == null || bid === '') return null;
    const bins = entry.bins ?? entry.Bins ?? [];
    const bin = bins.find((b) => Number(b?.binId ?? b?.BinId) === Number(bid));
    if (!bin) return null;
    const nm = String(bin?.name ?? bin?.Name ?? '').trim();
    return nm || null;
}

/** Select value `si:{id}` → BTS cable name for ReportTemperatureComparisonData; plain string → pass through (legacy). */
export function resolveCableNameForReportApi(siteInfos, selectedSiteGuid, filterCableSelection) {
    if (filterCableSelection == null || filterCableSelection === '') return null;
    const s = String(filterCableSelection);
    if (s.startsWith('si:')) {
        const id = s.slice(3);
        const rows = getSiteInfosCableRows(siteInfos, selectedSiteGuid, '');
        const row = rows.find((r) => String(r.id) === String(id));
        return row?.cableName ?? null;
    }
    const t = s.trim();
    return t || null;
}
